import { Search, Loader2, X } from 'lucide-react';
import { useAutocomplete, type UseAutocompleteOptions } from '../hooks/useAutocomplete';
import { useEffect } from 'react';
import * as React from 'react';

interface AutocompleteProps<T> extends UseAutocompleteOptions<T> {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  renderItem?: (item: T, isActive: boolean) => React.ReactNode;
  onInputChange?: (value: string) => void;
  getKey?: (item: T) => string | number;
}

export function Autocomplete<T>({
  id,
  initialItem,
  getLabel,
  placeholder = 'Search...',
  disabled = false,
  className = '',
  renderItem,
  onInputChange,
  getKey,
  ...hookOptions
}: AutocompleteProps<T>) {

  const lastSelectedLabelRef = React.useRef<string | null>(null);

  // Wrap onSelect to track when an item is selected
  const originalOnSelect = hookOptions.onSelect;
  const wrappedOnSelect = React.useCallback((item: T) => {
    lastSelectedLabelRef.current = getLabel(item);
    originalOnSelect(item);
  }, [originalOnSelect, getLabel]);

  const ac = useAutocomplete<T>({ ...hookOptions, getLabel, onSelect: wrappedOnSelect });

  // Apply initial item to the autocomplete
  const applyInitialItem = (item: T) => {
    ac.setQuery(getLabel(item));
    ac.setResults([item]);
    ac.setIsOpen(false);
    ac.setActiveIndex(-1);
  };

  // Set initial item when editing an existing item
  useEffect(() => {
    if (initialItem) {
      // Use applyInitialItem to set the initial item, which will update query
      applyInitialItem(initialItem);
      lastSelectedLabelRef.current = getLabel(initialItem);
    }
  }, [initialItem, getLabel]);

  const listboxId = `${id}-listbox`;

  return (
    <div ref={ac.containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-steel" />
        <input
          id={id}
          ref={ac.inputRef}
          type="text"
          value={ac.query}
          onChange={(e) => {
            ac.handleInputChange(e);
            // Clear the selected label ref when user manually types
            // This ensures onBlur will save the value if it differs from selected item
            if (lastSelectedLabelRef.current !== null && lastSelectedLabelRef.current !== e.target.value) {
              lastSelectedLabelRef.current = null;
            }
            onInputChange?.(e.target.value);
          }}
          onBlur={() => {
            // Save the current input value when user leaves the field
            // Only save if the value differs from the last selected item's label
            // This prevents overwriting the code with the name when an item was selected
            if (lastSelectedLabelRef.current !== ac.query) {
              onInputChange?.(ac.query);
            }
          }}
          onKeyDown={ac.handleKeyDown}
          onFocus={ac.handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={ac.isOpen}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={ac.activeIndex >= 0 ? `${id}-option-${ac.activeIndex}` : undefined}
          className="w-full pl-10 pr-9 py-2 bg-white border border-steel/30 rounded-md
                     placeholder-steel/50 text-navy
                     focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate
                     disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
        />
        {ac.isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-steel animate-spin" />
        )}
        {!ac.isLoading && ac.query.length > 0 && (
          <button
            type="button"
            onClick={() => {
              ac.clear();
              lastSelectedLabelRef.current = null;
              onInputChange?.('');
              ac.inputRef.current?.focus();
              ac.setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-steel hover:text-navy transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {ac.isOpen && (
        <ul
          ref={ac.listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto
                     bg-white border border-steel/20 rounded-md shadow-lg"
        >
          {ac.results.map((item, index) => (
            <li
              key={getKey ? getKey(item) : index}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === ac.activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                ac.selectItem(item);
              }}
              onMouseEnter={() => ac.setActiveIndex(index)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === ac.activeIndex ? 'bg-navy/5 text-navy' : 'text-navy hover:bg-navy/5'
              }`}
            >
              {renderItem ? (
                renderItem(item, index === ac.activeIndex)
              ) : (
                <div className="text-sm font-medium">{getLabel(item)}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
